/* ==========================================================================
   sticky-atc.js
   --------------------------------------------------------------------------
   Behaviour for the floating CTA bar (snippets/sticky-atc-bar.liquid).

   Responsibilities
     1. Reveal / hide the bar once the main buy box scrolls out of view.
     2. Keep the bar's selector and the main product form in sync, both ways.
     3. Add to cart straight from the bar.

   Two product shapes, switched on data-mode:

     "bundle"   Panel product tagged 'product-bundle'. The mounting options are
                variants of OTHER products (custom.stand_options), so a purchase
                is TWO line items: the panel (carrying the chosen mounting label
                as a property) and the stand (carrying the panel name).
                This mirrors assets/stand-options.js exactly — same property
                keys, same order — so carts built from either selector match.

     "variants" Ordinary Shopify variants. One line item.

   Sync notes
     - The main form is a Vue inline-template (<wetheme-product-form>). Vue
       writes select.value directly through v-model, which fires no DOM event,
       so the main -> bar direction is read by a cheap poll rather than a
       listener. The bar -> main direction dispatches a real 'change' so Vue
       picks it up.
     - In bundle mode the bar drives the main selector by clicking the matching
       .swatch-element, which lets stand-options.js do the price/image swap it
       already owns instead of duplicating that logic here.

   Debug: window.STICKY_CTA_DEBUG = true
   ========================================================================== */
(function () {
  'use strict';

  var DEBUG = !!window.STICKY_CTA_DEBUG;
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[sticky-cta]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  var POLL_MS = 300;

  /* Ignore scroll movements smaller than this so a micro-scroll, a trackpad
     nudge or a rubber-band bounce cannot make the header and bar flicker. */
  var SCROLL_BUFFER = 8;

  /* The visible header is not always the element that carries the sticky
     position: on the index template .header-section has height 0 and the
     93px header overflows it. Publish whichever one actually has height. */
  function measureHeader() {
    var hdr = document.querySelector('.header-section');
    if (!hdr) return;

    // 1. Product templates: the sticky element has the height itself.
    var h = hdr.getBoundingClientRect().height;

    // 2. Index/collection on desktop: it collapses to 0 and .site-header
    //    carries the height instead.
    if (!h) {
      var inner = hdr.querySelector('.site-header');
      if (inner) h = inner.getBoundingClientRect().height;
    }

    // 3. Index on mobile: .site-header is display:none and a separate mobile
    //    bar is painted, so fall back to the laid-out content height.
    if (!h) h = hdr.scrollHeight;

    // Guard against picking up the off-canvas menu, which is far taller.
    if (h > 0 && h <= 200) {
      document.documentElement.style.setProperty('--scta-header-h', Math.round(h) + 'px');
    }
  }

  /* Shopify's native privacy banner. While it is up it owns the screen, so
     the bar stays suppressed and the header stays put. */
  function cookieBannerActive() {
    var b = document.getElementById('shopify-pc__banner');
    if (!b || !b.offsetHeight) return false;
    var cs = getComputedStyle(b);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  /* ------------------------------------------------------------------ utils */

  function money(el, attr) { return el ? el.getAttribute(attr) : null; }

  function setText(node, value) {
    if (node && value != null) node.textContent = value;
  }

  function show(node, visible) {
    if (!node) return;
    if (visible) node.removeAttribute('hidden');
    else node.setAttribute('hidden', 'hidden');
  }

  /* --------------------------------------------------------------- instance */

  function StickyCta(root) {
    this.root = root;
    this.mode = root.getAttribute('data-mode') || 'variants';
    this.saleDisplay = root.getAttribute('data-sale-display') || 'auto';
    this.cartUrl = root.getAttribute('data-cart-url') || '/cart';

    this.select = root.querySelector('[data-sticky-select]');
    this.addBtn = root.querySelector('[data-sticky-add]');
    this.priceEl = root.querySelector('[data-sticky-price]');
    this.compareEl = root.querySelector('[data-sticky-compare]');
    this.savingsEl = root.querySelector('[data-sticky-savings]');
    this.ribbonEl = root.querySelector('[data-sticky-ribbon]');
    this.ribbonTextEl = root.querySelector('[data-sticky-ribbon-text]');
    this.financeEl = root.querySelector('[data-sticky-finance]');
    this.errorEl = root.querySelector('[data-sticky-error]');
    this.liveEl = root.querySelector('[data-sticky-live]');

    this.visible = false;
    this.syncing = false;
    this.lastMainValue = null;
    this.lastY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.blocked = false;

    this.bindEvents();
    this.watchBuyBox();
    this.startPoll();

    // Adopt whatever the main form already has selected on load.
    this.pullFromMain();

    log('init', this.mode);
  }

  /* ------------------------------------------------------------ visibility */

  /* The element we watch: the real add-to-cart button in the buy box. */
  StickyCta.prototype.findAnchor = function () {
    return document.getElementById('StandAddToCart') ||
           document.getElementById('AddToCart') ||
           document.querySelector('.paymentButtonsWrapper') ||
           document.querySelector('wetheme-product-form');
  };

  /* Smart scroll.

     Two independent conditions decide whether the bar is up:

       1. Entry threshold — the static buy-box button must have scrolled off
          the top. Above that point the page behaves normally: header visible,
          no bar, whichever way the reader moves.

       2. Scroll direction — past the threshold, scrolling DOWN means the
          reader is consuming content, so the header gives way to the bar;
          scrolling UP means they want navigation, so the bar gives way to the
          header. Movements under SCROLL_BUFFER are ignored.

     The anchor is re-resolved every time rather than observed once: the buy
     box lives inside <wetheme-product-form>, a Vue inline-template that
     rebuilds its subtree after hydration, so any node captured at boot ends
     up detached. Reading the rect also works in themes that scroll an inner
     container, where window scroll offsets stay at 0. */
  StickyCta.prototype.updateVisibility = function () {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;

    // The cookie banner outranks everything until it is dismissed.
    var blocked = cookieBannerActive();
    if (blocked !== this.blocked) {
      this.blocked = blocked;
      document.body.classList.toggle('sticky-atc-blocked', blocked);
    }
    if (blocked) { this.lastY = y; this.setVisible(false); return; }

    var past;
    var anchor = this.findAnchor();
    if (anchor) {
      var r = anchor.getBoundingClientRect();
      if (!r.width && !r.height) return;      // not laid out yet
      past = r.bottom < 0;
    } else {
      past = y > window.innerHeight * 0.6;    // no buy box on this page
    }

    if (!past) { this.lastY = y; this.setVisible(false); return; }

    var delta = y - this.lastY;
    if (Math.abs(delta) < SCROLL_BUFFER) return;
    this.lastY = y;

    this.setVisible(delta > 0);
  };

  StickyCta.prototype.watchBuyBox = function () {
    var self = this;
    var ticking = false;

    this.onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        self.updateVisibility();
      });
    };

    // Capture phase: scroll events from an inner scroller do not bubble.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.updateVisibility();
  };

  StickyCta.prototype.setVisible = function (visible) {
    // A bar hidden by the desktop-only / mobile-only setting has no layout
    // box, and must not drag the header off-screen on its behalf.
    if (visible && !this.root.offsetHeight) visible = false;
    if (visible === this.visible) return;

    this.visible = visible;
    this.root.classList.toggle('is-visible', visible);
    this.root.setAttribute('aria-hidden', visible ? 'false' : 'true');

    /* One class on <body> drives the rest of the page chrome from CSS:
       the header slides out, the chat bubble and locale switcher lift. */
    document.body.classList.toggle('show-sticky', visible);
    document.body.classList.toggle('sticky-hide-header', visible);
    this.updateLift();
  };

  /* Publish the bar's height so the floating widgets can clear it.
     Daniel's spec: bar height plus a 16px buffer. */
  StickyCta.prototype.updateLift = function () {
    measureHeader();
    var lift = this.visible ? this.root.offsetHeight + 16 : 0;
    document.documentElement.style.setProperty('--scta-bar-lift', lift + 'px');
  };

  /* --------------------------------------------------------------- pricing */

  StickyCta.prototype.applyOption = function (opt) {
    if (!opt) return;

    setText(this.priceEl, money(opt, 'data-price'));

    var onSale = opt.getAttribute('data-on-sale') === 'true';
    var savings = opt.getAttribute('data-savings') || '';
    var manual = this.saleDisplay === 'manual';
    var off = this.saleDisplay === 'off';

    if (this.compareEl) {
      var compare = money(opt, 'data-compare');
      if (!off && onSale && compare) {
        setText(this.compareEl, compare);
        show(this.compareEl, true);
      } else {
        show(this.compareEl, false);
      }
    }

    // In manual mode the copy is authored in the section and never recalculated,
    // so leave whatever Liquid rendered in place.
    if (this.savingsEl && !manual) {
      if (!off && onSale && savings) {
        setText(this.savingsEl, savings);
        show(this.savingsEl, true);
      } else {
        show(this.savingsEl, false);
      }
    }

    if (this.ribbonEl && !manual && !off) {
      var ribbon = opt.getAttribute('data-ribbon') || '';
      if (this.ribbonTextEl && ribbon) setText(this.ribbonTextEl, ribbon);
      show(this.ribbonEl, onSale && !!ribbon);
    }

    this.refreshAffirm(opt.getAttribute('data-affirm'));

    // Sold-out state on the button.
    var available = opt.getAttribute('data-available');
    if (this.addBtn && available !== null) {
      this.addBtn.disabled = (available === 'false') || opt.disabled;
    }

    log('applied', opt.value, money(opt, 'data-price'));
  };

  /* The financing line is the theme's real Affirm widget, not static text.
     Affirm reads data-amount in DOLLARS and repaints on affirm.ui.refresh().
     It also stamps data-processed once it has rendered, which has to be
     cleared or the refresh is a no-op. */
  StickyCta.prototype.refreshAffirm = function (amount) {
    if (!this.financeEl || !amount) return;
    if (this.financeEl.getAttribute('data-amount') === String(amount)) return;

    this.financeEl.setAttribute('data-amount', amount);
    this.financeEl.removeAttribute('data-processed');
    try {
      if (window.affirm && window.affirm.ui && window.affirm.ui.refresh) window.affirm.ui.refresh();
    } catch (e) { log('affirm refresh failed', e); }
  };

  StickyCta.prototype.currentOption = function () {
    if (!this.select) return null;
    return this.select.options[this.select.selectedIndex] || null;
  };

  /* ------------------------------------------------------------------ sync */

  /* Main product form -> bar */
  StickyCta.prototype.pullFromMain = function () {
    if (this.syncing || !this.select) return;

    if (this.mode === 'bundle') {
      var wrap = document.querySelector('[data-mounting-options]');
      if (!wrap) return;
      var active = wrap.querySelector('.swatch-element.active');
      if (!active) return;

      var standId = active.getAttribute('data-stand-id') || '';
      var wanted = standId || 'panel';
      if (wanted === this.select.value) return;

      this.syncing = true;
      this.select.value = wanted;
      this.applyOption(this.currentOption());
      this.syncing = false;
      log('pulled bundle', wanted);
      return;
    }

    var mainSelect = this.mainVariantSelect();
    if (!mainSelect || !mainSelect.value) return;
    if (mainSelect.value === this.lastMainValue) return;

    this.lastMainValue = mainSelect.value;
    if (mainSelect.value === this.select.value) return;

    this.syncing = true;
    this.select.value = mainSelect.value;
    this.applyOption(this.currentOption());
    this.syncing = false;
    log('pulled variant', mainSelect.value);
  };

  StickyCta.prototype.mainVariantSelect = function () {
    return document.querySelector('select[name="id"].original-select') ||
           document.querySelector('select[name="id"]');
  };

  /* Bar -> main product form */
  StickyCta.prototype.pushToMain = function () {
    var opt = this.currentOption();
    if (!opt) return;

    this.syncing = true;

    if (this.mode === 'bundle') {
      var wrap = document.querySelector('[data-mounting-options]');
      if (wrap) {
        var standId = opt.getAttribute('data-stand-id') || '';
        var target = standId
          ? wrap.querySelector('.swatch-element[data-stand-id="' + standId + '"]')
          : wrap.querySelector('.swatch-element[data-add-stand="false"]');
        // Clicking lets stand-options.js run its own price + image swap.
        if (target && !target.classList.contains('active')) target.click();
      }
    } else {
      var mainSelect = this.mainVariantSelect();
      if (mainSelect && mainSelect.value !== this.select.value) {
        mainSelect.value = this.select.value;
        mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.lastMainValue = this.select.value;
      }
    }

    var self = this;
    // Release the guard after the main form has settled.
    setTimeout(function () { self.syncing = false; }, 80);
  };

  StickyCta.prototype.startPoll = function () {
    var self = this;
    this.pollId = setInterval(function () {
      self.pullFromMain();
      self.updateVisibility();
      if (self.visible) self.updateLift();
    }, POLL_MS);
  };

  /* ------------------------------------------------------------ add to cart */

  StickyCta.prototype.quantity = function () {
    var q = document.getElementById('Quantity') ||
            document.querySelector('[name="quantity"]');
    var n = parseInt(q && q.value, 10);
    return (!n || n < 1) ? 1 : n;
  };

  // Pre-order flag from the main product form. Same input stand-options.js reads,
  // so a pre-order panel carries the same property whichever button adds it.
  StickyCta.prototype.orderType = function () {
    var f = document.querySelector('#AddToCartForm input[name="properties[Order type]"]');
    return f ? f.value : '';
  };

  StickyCta.prototype.buildItems = function () {
    var opt = this.currentOption();
    var qty = this.quantity();
    var orderType = this.orderType();

    if (this.mode === 'bundle') {
      var panelId = this.root.getAttribute('data-panel-id');
      var panelName = this.root.getAttribute('data-panel-name') || '';
      var key = this.root.getAttribute('data-property-key') || 'Mounting Option';

      var label = opt ? (opt.textContent || '').trim() : '';
      var panelProps = {};
      if (label) panelProps[key] = label;
      if (orderType) panelProps['Order type'] = orderType;

      var items = [{ id: panelId, quantity: qty, properties: panelProps }];

      var addStand = opt && opt.getAttribute('data-add-stand') === 'true';
      var standId = opt && opt.getAttribute('data-stand-id');
      if (addStand && standId) {
        var standProps = {};
        if (panelName) standProps['Panel'] = panelName;
        items.push({ id: standId, quantity: qty, properties: standProps });
      }
      return items;
    }

    var variantId = opt ? opt.value : (this.select && this.select.value);
    if (!variantId) return null;
    return [{ id: variantId, quantity: qty }];
  };

  StickyCta.prototype.setError = function (message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      show(this.errorEl, true);
    } else {
      show(this.errorEl, false);
    }
  };

  StickyCta.prototype.addToCart = function () {
    var self = this;
    var items = this.buildItems();
    if (!items) { this.setError('Please choose an option first.'); return; }

    this.setError('');
    this.addBtn.classList.add('is-loading');
    this.addBtn.disabled = true;
    log('adding', items);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok || (res.body && res.body.status && res.body.status !== 200)) {
          throw new Error((res.body && (res.body.description || res.body.message)) ||
                          'This item could not be added to your cart.');
        }
        if (self.liveEl) self.liveEl.textContent = 'Added to cart';
        window.location.href = self.cartUrl;
      })
      .catch(function (err) {
        self.addBtn.classList.remove('is-loading');
        self.addBtn.disabled = false;
        self.setError((err && err.message) || 'This item could not be added to your cart.');
        log('add failed', err);
      });
  };

  /* ---------------------------------------------------------------- events */

  StickyCta.prototype.bindEvents = function () {
    var self = this;

    if (this.select) {
      this.select.addEventListener('change', function () {
        if (self.syncing) return;
        self.setError('');
        self.applyOption(self.currentOption());
        self.pushToMain();
      });
    }

    if (this.addBtn) {
      // Capture phase: the Vue form stops propagation on bubble for its own
      // buttons, and stand-options.js already listens in capture.
      this.addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.addToCart();
      });
    }
  };

  StickyCta.prototype.destroy = function () {
    document.body.classList.remove('show-sticky', 'sticky-hide-header', 'sticky-atc-blocked');
    document.documentElement.style.removeProperty('--scta-bar-lift');
    if (this.onScroll) {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.pollId) clearInterval(this.pollId);
  };

  /* ------------------------------------------------------------------ boot */

  var instances = [];

  function boot() {
    instances.forEach(function (i) { i.destroy(); });
    instances = [];
    document.querySelectorAll('[data-sticky-cta]').forEach(function (el) {
      // A transformed / filtered / overflow-clipped ancestor re-bases
      // position:fixed onto that ancestor, so the bar can end up parked
      // inside a section box and never appear. Hoisting to <body> removes
      // the whole class of problem and guarantees the z-index applies.
      if (el.parentNode !== document.body) document.body.appendChild(el);
      instances.push(new StickyCta(el));
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Theme editor: re-bind when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-sticky-cta]')) boot();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var el = e.target && e.target.querySelector('[data-sticky-cta]');
    if (el) el.classList.add('is-visible');
  });
})();
